/**
 * Duffel Flights API — never put DUFFEL_API_KEY in the frontend.
 *
 * When REACT_APP_API_URL points at Supabase (…supabase.co…), flight calls POST to
 * REACT_APP_SUPABASE_URL/functions/v1/duffel-flights with anon apikey (plain fetch — not
 * supabase.functions.invoke — so we avoid extra headers like x-client-info that break CORS).
 *
 * Set REACT_APP_DUFFEL_FLIGHTS_USE_FETCH=true to force HTTP to tourasco-backend instead.
 */

import { getBackendApiUrl, getApiHeaders } from '../services/apiConfig';

const getHeaders = () => getApiHeaders();

const DUFFEL_FLIGHTS_FN =
  process.env.REACT_APP_SUPABASE_DUFFEL_FLIGHTS_FUNCTION || 'duffel-flights';

const parseResponsePayload = async (response) => {
  const raw = await response.text();
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return { error: raw };
  }
};

/** Headers Supabase Edge gateway accepts without triggering blocked optional headers on preflight. */
function getDuffelEdgeFunctionHeaders() {
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
  const headers = {
    'Content-Type': 'application/json',
  };
  if (key) {
    headers.Authorization = `Bearer ${key}`;
    headers.apikey = key;
  }
  return headers;
}

function shouldInvokeFlightsViaSupabase() {
  if (process.env.REACT_APP_DUFFEL_FLIGHTS_USE_FETCH === 'true') return false;
  return getBackendApiUrl().includes('supabase.co');
}

async function invokeFlights(body) {
  const base = process.env.REACT_APP_SUPABASE_URL?.replace(/\/+$/, '');
  if (!base) {
    throw new Error(
      'Set REACT_APP_SUPABASE_URL (and REACT_APP_SUPABASE_ANON_KEY) for flight search via Edge Functions.',
    );
  }
  const url = `${base}/functions/v1/${DUFFEL_FLIGHTS_FN}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: getDuffelEdgeFunctionHeaders(),
    body: JSON.stringify(body),
  });
  const data = await parseResponsePayload(response);
  if (!response.ok) {
    const msg = data?.error || data?.message || `HTTP ${response.status}`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  if (data && data.success === false && data.error) {
    throw new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
  }
  return data;
}

/**
 * Create flight offer request (search). Returns Duffel offer_request including offers[].
 */
export async function createFlightOfferRequest(payload = {}) {
  if (shouldInvokeFlightsViaSupabase()) {
    return invokeFlights({ action: 'offer_request', ...payload });
  }

  const response = await fetch(`${getBackendApiUrl()}/api/duffel/flights/offer-requests`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await parseResponsePayload(response);
  if (!response.ok) {
    let errorMsg = data?.error || data?.message || `HTTP ${response.status}`;
    if (typeof errorMsg !== 'string') errorMsg = JSON.stringify(errorMsg);
    if (response.status === 404) {
      errorMsg =
        'Flight search route not found on this backend. Deploy the `duffel-flights` Edge Function ' +
        '(supabase functions deploy duffel-flights) or redeploy `tourasco-backend` with latest code.';
    }
    throw new Error(errorMsg);
  }
  return data;
}

/**
 * Refresh a single offer before passenger details / booking.
 */
export async function getFlightOffer(offerId) {
  if (!offerId) throw new Error('offerId is required');

  if (shouldInvokeFlightsViaSupabase()) {
    return invokeFlights({ action: 'get_offer', offer_id: offerId });
  }

  const response = await fetch(
    `${getBackendApiUrl()}/api/duffel/flights/offers/${encodeURIComponent(offerId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    },
  );
  const data = await parseResponsePayload(response);
  if (!response.ok) {
    const errorMsg = data?.error || `HTTP ${response.status}`;
    throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
  }
  return data;
}

/**
 * Create order (booking). Server refreshes offer price before charging Duffel balance.
 */
export async function createFlightOrder(payload = {}) {
  if (shouldInvokeFlightsViaSupabase()) {
    return invokeFlights({ action: 'create_order', ...payload });
  }

  const response = await fetch(`${getBackendApiUrl()}/api/duffel/flights/orders`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await parseResponsePayload(response);
  if (!response.ok) {
    const errorMsg = data?.error || `HTTP ${response.status}`;
    throw new Error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
  }
  return data;
}

/**
 * Optional: call standalone edge function explicitly (same as internal invoke path).
 */
export async function invokeDuffelFlightsEdge(body) {
  return invokeFlights(body);
}

export function buildFlightPassengers(adults, childAges = []) {
  const passengers = [];
  const n = Math.max(1, Number(adults) || 1);
  for (let i = 0; i < n; i += 1) {
    passengers.push({ type: 'adult' });
  }
  const ages = Array.isArray(childAges) ? childAges : [];
  ages.forEach((age) => {
    const a = Number(age);
    if (Number.isFinite(a) && a >= 0 && a < 18) {
      passengers.push({ age: Math.floor(a) });
    }
  });
  return passengers;
}

/** Summarize offer for a result card (Duffel offer object). */
export function summarizeFlightOffer(offer) {
  if (!offer) return null;
  const slices = offer.slices || [];
  const rows = slices.map((slice) => {
    const segments = slice?.segments || [];
    const first = segments[0];
    const last = segments[segments.length - 1];
    const stops = Math.max(0, segments.length - 1);
    const origin = first?.origin?.iata_code || first?.origin?.city?.iata_code || '—';
    const dest = last?.destination?.iata_code || last?.destination?.city?.iata_code || '—';
    const dep = first?.departing_at || '';
    const arr = last?.arriving_at || '';
    const marketing =
      first?.marketing_carrier?.name ||
      first?.operating_carrier?.name ||
      offer.owner?.name ||
      '';
    return { origin, dest, dep, arr, stops, marketing };
  });
  return {
    id: offer.id,
    total_amount: offer.total_amount,
    total_currency: offer.total_currency,
    slices: rows,
    raw: offer,
  };
}
